import { useState, useEffect, useRef, useCallback } from 'react';
import { playLaunchSound } from '../utils/audioSynth';

export interface PhysicsState {
  x: number;       // current left offset px
  y: number;       // current top offset px
  vx: number;      // horizontal velocity px/frame
  vy: number;      // vertical velocity px/frame
  rotation: number;  // current deg
  vr: number;      // rotational velocity deg/frame
  hue: number;     // hue-rotate deg for color tint
  el: HTMLElement;
  rect: DOMRect;   // original bounding rect
}

export interface AntigravityControls {
  isActive: boolean;
  countdown: number | null;
  launch: () => void;
  reset: () => void;
}

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function collectVisibleElements(): { el: HTMLElement; rect: DOMRect }[] {
  if (typeof window === 'undefined') return [];
  const elements = Array.from(
    document.querySelectorAll('body *:not(script):not(style):not([data-antigravity-ignore])')
  ) as HTMLElement[];

  return elements
    .filter((el) => {
      const style = window.getComputedStyle(el);
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0'
      );
    })
    .map((el) => ({
      el,
      rect: el.getBoundingClientRect(),
    }));
}

function freezeAndInitializePhysics(
  measurements: { el: HTMLElement; rect: DOMRect }[],
  map: Map<string, PhysicsState>
) {
  map.clear();
  measurements.forEach(({ el, rect }) => {
    const id = generateUUID();
    el.setAttribute('data-antigravity-id', id);

    // Freeze element
    el.style.width = `${rect.width}px`;
    el.style.height = `${rect.height}px`;
    el.style.left = `${rect.left}px`;
    el.style.top = `${rect.top}px`;
    el.style.position = 'fixed';
    el.style.margin = '0';

    const hue = Math.floor(Math.random() * 61);
    el.style.filter = `hue-rotate(${hue}deg)`;

    const vx = (Math.random() * 3 + 2) * (Math.random() < 0.5 ? -1 : 1);
    const vy = -(Math.random() * 5 + 3);
    const vr = (Math.random() * 3 + 1) * (Math.random() < 0.5 ? -1 : 1);

    map.set(id, {
      x: 0,
      y: 0,
      vx,
      vy,
      rotation: 0,
      vr,
      hue,
      el,
      rect,
    });
  });
}

function updatePhysics(map: Map<string, PhysicsState>, gravity: number, width: number, height: number) {
  map.forEach((state, id) => {
    state.vy += gravity;
    state.x += state.vx;
    state.y += state.vy;
    state.rotation += state.vr;

    state.el.style.transform = `translate(${state.x}px, ${state.y}px) rotate(${state.rotation}deg)`;

    const currentTop = state.rect.top + state.y;
    const currentBottom = state.rect.bottom + state.y;
    const currentLeft = state.rect.left + state.x;
    const currentRight = state.rect.right + state.x;

    const isOffScreen =
      currentBottom < -200 ||
      currentTop > height + 200 ||
      currentRight < -200 ||
      currentLeft > width + 200;

    if (isOffScreen) {
      map.delete(id);
    }
  });
}

export function useAntigravity(): AntigravityControls {
  const [isActive, setIsActive] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const physicsMapRef = useRef<Map<string, PhysicsState>>(new Map());
  const animFrameIdRef = useRef<number | null>(null);

  const startPhysics = useCallback(() => {
    if (typeof window === 'undefined') return;

    try {
      playLaunchSound();
    } catch (e) {
      console.warn('Could not play launch sound:', e);
    }

    document.body.style.overflow = 'hidden';

    const measurements = collectVisibleElements();

    requestAnimationFrame(() => {
      freezeAndInitializePhysics(measurements, physicsMapRef.current);
      setIsActive(true);

      const gravity = 0.35;
      const animate = () => {
        const map = physicsMapRef.current;
        updatePhysics(map, gravity, window.innerWidth, window.innerHeight);

        if (map.size > 0) {
          animFrameIdRef.current = requestAnimationFrame(animate);
        } else {
          setIsActive(false);
        }
      };

      animFrameIdRef.current = requestAnimationFrame(animate);
    });
  }, []);

  const launch = useCallback(() => {
    // Accessibility Guard: Immediately abort heavy physics/3D if user prefers reduced motion
    const prefersReducedMotion = typeof window !== 'undefined' 
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
      : false;

    if (prefersReducedMotion) {
      console.warn('Antigravity sequence aborted: prefers-reduced-motion is enabled.');
      return;
    }

    if (countdown !== null || isActive) return;
    setCountdown(3);
  }, [countdown, isActive]);

  const reset = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (countdown === null) return;

    if (countdown === 0) {
      const timer = setTimeout(() => {
        setCountdown(null);
        startPhysics();
      }, 500);
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, startPhysics]);

  // Keyboard shortcut effect
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        launch();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [launch]);

  // Clean up animation on unmount
  useEffect(() => {
    return () => {
      if (animFrameIdRef.current !== null) {
        cancelAnimationFrame(animFrameIdRef.current);
      }
    };
  }, []);

  return {
    isActive,
    countdown,
    launch,
    reset,
  };
}
